import type { WatchWizardCapabilities } from "../components/WatchCreateWizard";
import type {
  WatchAuthoringStructureCapability,
  WatchAuthoringStructureId,
  WatchContractsPayload,
} from "./watchTypes";
import {
  decodeWatchSignalCatalog,
  getAuthorableWatchSignals,
  type WatchSignalCatalog,
} from "./watchSignalCatalog";

const STRUCTURE_IDS: WatchAuthoringStructureId[] = [
  "single",
  "vertical",
  "iron_condor",
  "iron_butterfly",
];

const LEGACY_ENTRY_STRUCTURES: WatchAuthoringStructureId[] = [
  "vertical",
  "iron_condor",
  "iron_butterfly",
];

function structureIsAvailable(capability?: WatchAuthoringStructureCapability) {
  return capability?.enabled === true
    && capability.customer_visible === true
    && capability.rollout_mode !== "DISABLED"
    && capability.rollout_mode !== "SHADOW";
}

function enabledStructures(
  structures?: Partial<Record<WatchAuthoringStructureId, WatchAuthoringStructureCapability>>,
  options: { requireCustomerVisible?: boolean } = {},
) {
  if (!structures) return [];
  return STRUCTURE_IDS.filter((structure) => {
    const capability = structures[structure];
    return structureIsAvailable(capability)
      && (!options.requireCustomerVisible || capability?.customer_visible === true);
  });
}

function signalCatalogAllowsMarketPrice(
  contracts: WatchContractsPayload | null,
): boolean {
  // Older authenticated servers do not return the additive catalog. Preserve
  // their established authoring contract until the field is actually present.
  if (contracts?.signal_catalog === undefined) return true;

  const catalog = getWatchAuthoringSignalCatalog(contracts);
  if (!catalog) return false;
  return getAuthorableWatchSignals(catalog, {
    job: "MARKET",
    subjectScope: "UNDERLYING",
  }).some((signal) => (
    signal.signal_id === "underlying.price"
    && signal.definition_version === 1
  ));
}

function marketPriceIsAvailable(
  contracts: WatchContractsPayload | null,
  serverAuthoringEnabled: boolean,
): boolean {
  return serverAuthoringEnabled && signalCatalogAllowsMarketPrice(contracts);
}

/**
 * Converts the authenticated server contract into the small, customer-facing
 * capability model the wizard consumes. Unknown schema versions fail closed.
 */
export function getWatchWizardCapabilities(
  contracts: WatchContractsPayload | null,
): WatchWizardCapabilities {
  const authoringV2 = contracts?.authoring_v2;
  if (authoringV2?.schema_version === 2) {
    const positionGoal = authoringV2.goals?.position;
    const entryGoal = authoringV2.goals?.structure_entry;
    const positionStructures = enabledStructures(positionGoal?.structures);
    const entryStructures = enabledStructures(entryGoal?.structures, { requireCustomerVisible: true });
    const entryTargetTypes = Object.fromEntries(
      entryStructures.map((structure) => [
        structure,
        (entryGoal?.structures?.[structure]?.target_types || []).filter(
          (target): target is "NET_DEBIT_LTE" | "NET_CREDIT_GTE" => (
            target === "NET_DEBIT_LTE" || target === "NET_CREDIT_GTE"
          ),
        ),
      ]),
    );

    return {
      position: positionGoal?.enabled === true && positionStructures.length > 0,
      priceTarget: marketPriceIsAvailable(
        contracts,
        authoringV2.goals?.price_target?.enabled === true,
      ),
      setupEntry: entryGoal?.enabled === true && entryStructures.length > 0,
      positionStructures,
      entryStructures,
      entryTargetTypes,
    };
  }

  const authoringV1 = contracts?.authoring;
  return {
    position: true,
    priceTarget: marketPriceIsAvailable(
      contracts,
      authoringV1?.schema_version === 1
      && authoringV1.goals?.price_target?.enabled === true,
    ),
    setupEntry:
      authoringV1?.schema_version === 1
      && authoringV1.goals?.structure_entry?.enabled === true,
    positionStructures: STRUCTURE_IDS,
    // Schema v1 advertised structure entry as one broad multi-leg feature.
    // It cannot safely opt a user into the newer single-option contract.
    entryStructures: LEGACY_ENTRY_STRUCTURES,
  };
}

/**
 * Decode the additive signal catalog used to narrow server authoring authority.
 * Missing and malformed catalogs both return `null`; callers that require
 * backward-compatible absence semantics must inspect the raw field first.
 */
export function getWatchAuthoringSignalCatalog(
  contracts: WatchContractsPayload | null,
): WatchSignalCatalog | null {
  return decodeWatchSignalCatalog(contracts?.signal_catalog);
}
